import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Pagination,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Add, Clear, Search, Visibility } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import { useSupportCases } from "../hooks/useSupportCases";
import { useBookmarkableHubState } from "../hooks/useBookmarkableHubState";
import { useBookmarkableTab } from "../hooks/useBookmarkableTab";
import type { SupportCase } from "../types";
import { SupportCaseStatus } from "../types";
import {
  SUPPORT_CASE_STATUS_LABELS,
  supportCaseStatusColor,
} from "../utils/supportCaseLabels";

const PAGE_SIZE = 10;
const STATUS_OPTIONS = Object.values(SupportCaseStatus);

const SUPPORT_CASE_VIEW_SLUGS = ["mine", "queue"] as const;

const SupportCasesPage: React.FC = () => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const hubState = useBookmarkableHubState({ statusOptions: STATUS_OPTIONS });
  const { myCases, agentCases, showAgentQueue, isCsr, loading, error } =
    useSupportCases();
  const { tabIndex: tab } = useBookmarkableTab({
    slugs: SUPPORT_CASE_VIEW_SLUGS,
    paramName: "view",
  });

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(
          "view",
          SUPPORT_CASE_VIEW_SLUGS[value] ?? SUPPORT_CASE_VIEW_SLUGS[0],
        );
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  const activeList = tab === 0 || !showAgentQueue ? myCases : agentCases;
  const tabLabel = tab === 0 ? "My cases" : isCsr ? "All cases (CSR)" : "Agent queue";

  const filtered = useMemo(() => {
    let result = activeList;
    if (hubState.debouncedQ) {
      const q = hubState.debouncedQ.toLowerCase();
      result = result.filter((item) =>
        [
          item.name,
          item.description ?? "",
          item.createdBy,
          item.assigneeEmail ?? "",
          item.product?.name ?? "",
        ].some((f) => (f ?? "").toLowerCase().includes(q)),
      );
    }
    if (hubState.statuses.length) {
      result = result.filter((item) =>
        hubState.statuses.includes(item.caseStatus),
      );
    }
    return result;
  }, [activeList, hubState]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(hubState.page, totalPages);
  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const returnSearch = hubState.searchString;

  const renderRow = (supportCase: SupportCase) => (
    <TableRow
      key={supportCase.uuid}
      hover
      sx={{ cursor: "pointer" }}
      onClick={() =>
        navigate(`/support-cases/${supportCase.uuid}${returnSearch}`)
      }
    >
      <TableCell>
        <Typography variant="body2" fontWeight={600} noWrap>
          {supportCase.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={SUPPORT_CASE_STATUS_LABELS[supportCase.caseStatus]}
          size="small"
          color={supportCaseStatusColor(supportCase.caseStatus)}
        />
      </TableCell>
      <TableCell>{supportCase.assigneeEmail ?? "—"}</TableCell>
      <TableCell>{supportCase.product?.name ?? "—"}</TableCell>
      <TableCell>{supportCase.createdBy}</TableCell>
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <Tooltip title="View details">
          <IconButton
            size="small"
            onClick={() =>
              navigate(`/support-cases/${supportCase.uuid}${returnSearch}`)
            }
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Support Cases"
        pageDescription="Create and track support cases. Agents can claim, assign, and resolve cases through the workflow."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {showAgentQueue && (
          <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="My cases" />
            <Tab label={tabLabel} />
          </Tabs>
        )}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <TextField
            size="small"
            placeholder="Search cases..."
            value={hubState.q}
            onChange={(e) => hubState.setQ(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: hubState.q ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => hubState.setQ("")}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(`/support-cases/add${returnSearch}`)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            New case
          </Button>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Status
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {STATUS_OPTIONS.map((status) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    size="small"
                    checked={hubState.statuses.includes(status)}
                    onChange={() => hubState.toggleStatus(status)}
                  />
                }
                label={SUPPORT_CASE_STATUS_LABELS[status]}
              />
            ))}
          </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                      Title
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                      Assignee
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                      Product
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                      Created by
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "text.primary" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{
                          py: 4,
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? theme.palette.grey[300]
                              : theme.palette.text.secondary,
                          borderBottom: "none",
                        }}
                      >
                        <Typography variant="body2" component="span" sx={{ color: "inherit" }}>
                          No cases match your filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map(renderRow)
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {filtered.length > PAGE_SIZE && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => hubState.setPage(value)}
                  size="small"
                />
              </Box>
            )}
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default SupportCasesPage;
